import { m } from "@mojo/i18n";
import { createFileRoute } from "@tanstack/react-router";
import {
	Check,
	Info,
	Pencil,
	Plus,
	SquareStack,
	Trash2,
	Users,
	X,
} from "lucide-react";
import { useState } from "react";
import { PageHeader } from "@/components/layout";
import { Badge, Button } from "@/components/ui";
import {
	type Actor,
	createActor,
	deleteActor,
	type Epic,
	getActors,
	getEpics,
	updateActor,
} from "@/data/settings";

// Note: Epics are read-only (synced from Linear)

export const Route = createFileRoute("/_authenticated/settings")({
	loader: async () => {
		const [actors, epics] = await Promise.all([getActors(), getEpics()]);
		return { actors, epics };
	},
	component: SettingsPage,
});

const colorOptions = [
	{ value: "#3b82f6", label: "Blue" },
	{ value: "#10b981", label: "Green" },
	{ value: "#8b5cf6", label: "Purple" },
	{ value: "#f59e0b", label: "Amber" },
	{ value: "#ec4899", label: "Pink" },
	{ value: "#ef4444", label: "Red" },
	{ value: "#06b6d4", label: "Cyan" },
	{ value: "#14b8a6", label: "Teal" },
];

function SettingsPage() {
	const { actors: initialActors, epics } = Route.useLoaderData();

	const [actors, setActors] = useState<Actor[]>(initialActors);

	return (
		<div>
			<PageHeader
				title={m.settings_title()}
				description={m.settings_description()}
			/>

			<div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
				{/* Epics Section (read-only from Linear) */}
				<EpicsSection epics={epics} />

				{/* Actors Section */}
				<ActorsSection actors={actors} setActors={setActors} />
			</div>

			{/* Info Note */}
			<div className="mt-8 rounded-lg border border-border bg-bg-secondary/50 p-4">
				<div className="flex gap-3">
					<Info className="h-5 w-5 flex-shrink-0 text-text-tertiary" />
					<p className="text-sm text-text-secondary">
						<strong className="text-text-primary">Note:</strong>{" "}
						{m.settings_info_note()}
					</p>
				</div>
			</div>
		</div>
	);
}

// ============ EPICS SECTION (Read-only from Linear) ============

function EpicsSection({ epics }: { epics: Epic[] }) {
	return (
		<div className="overflow-hidden rounded-xl border border-border bg-bg-elevated">
			{/* Header */}
			<div className="border-border border-b px-6 py-4">
				<div className="flex items-center gap-3">
					<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10">
						<SquareStack className="h-4 w-4 text-accent" />
					</div>
					<div className="flex-1">
						<h2 className="font-semibold text-text-primary">
							{m.settings_epics()}
						</h2>
						<p className="text-text-secondary text-xs">
							{m.settings_epics_description()}
						</p>
					</div>
					<Badge variant="muted" size="sm">
						{epics.length}
					</Badge>
				</div>
			</div>

			{/* List */}
			<div className="max-h-96 divide-y divide-border overflow-y-auto">
				{epics.length > 0 ? (
					epics.map((epic) => (
						<div
							key={epic.id}
							className="flex items-center gap-3 px-6 py-3 transition-colors hover:bg-bg-hover/30"
						>
							<span className="flex-1 text-sm text-text-primary">
								{epic.name}
							</span>
						</div>
					))
				) : (
					<div className="px-6 py-8 text-center text-sm text-text-tertiary">
						{m.settings_empty_epics()}
					</div>
				)}
			</div>
		</div>
	);
}

// ============ ACTORS SECTION ============

function ActorsSection({
	actors,
	setActors,
}: {
	actors: Actor[];
	setActors: React.Dispatch<React.SetStateAction<Actor[]>>;
}) {
	const [newActorName, setNewActorName] = useState("");
	const [newActorColor, setNewActorColor] = useState("#3b82f6");
	const [editingActorId, setEditingActorId] = useState<string | null>(null);
	const [editingActorName, setEditingActorName] = useState("");
	const [editingActorColor, setEditingActorColor] = useState("");

	const handleAddActor = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!newActorName.trim()) return;

		try {
			const actor = await createActor({
				data: { name: newActorName.trim(), color: newActorColor },
			});
			setActors([...actors, actor]);
			setNewActorName("");
			setNewActorColor("#3b82f6");
		} catch (error) {
			console.error("Failed to create actor:", error);
		}
	};

	const startEditActor = (actor: Actor) => {
		setEditingActorId(actor.id);
		setEditingActorName(actor.name);
		setEditingActorColor(actor.color);
	};

	const saveEditActor = async () => {
		if (!editingActorId || !editingActorName.trim()) return;

		try {
			const updated = await updateActor({
				data: {
					id: editingActorId,
					name: editingActorName.trim(),
					color: editingActorColor,
				},
			});
			setActors(actors.map((a) => (a.id === editingActorId ? updated : a)));
			setEditingActorId(null);
			setEditingActorName("");
			setEditingActorColor("");
		} catch (error) {
			console.error("Failed to update actor:", error);
		}
	};

	const cancelEditActor = () => {
		setEditingActorId(null);
		setEditingActorName("");
		setEditingActorColor("");
	};

	const handleDeleteActor = async (id: string) => {
		try {
			await deleteActor({ data: { id } });
			setActors(actors.filter((a) => a.id !== id));
		} catch (error) {
			console.error("Failed to delete actor:", error);
		}
	};

	return (
		<div className="overflow-hidden rounded-xl border border-border bg-bg-elevated">
			{/* Header */}
			<div className="border-border border-b px-6 py-4">
				<div className="flex items-center gap-3">
					<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-feature/10">
						<Users className="h-4 w-4 text-feature" />
					</div>
					<div>
						<h2 className="font-semibold text-text-primary">
							{m.settings_actors()}
						</h2>
						<p className="text-text-secondary text-xs">
							{m.settings_actors_description()}
						</p>
					</div>
					<Badge variant="muted" size="sm">
						{actors.length}
					</Badge>
				</div>
			</div>

			{/* Add Form */}
			<div className="border-border border-b bg-bg-secondary/30 px-6 py-4">
				<form onSubmit={handleAddActor} className="flex gap-2">
					<input
						type="text"
						value={newActorName}
						onChange={(e) => setNewActorName(e.target.value)}
						placeholder="New actor name..."
						className="flex-1 rounded-lg border border-border bg-bg-secondary px-3 py-2 text-sm text-text-primary outline-none transition-all placeholder:text-text-tertiary focus:border-accent focus:ring-2 focus:ring-accent/20"
					/>
					<div className="relative">
						<select
							value={newActorColor}
							onChange={(e) => setNewActorColor(e.target.value)}
							className="min-w-28 cursor-pointer rounded-lg border border-border bg-bg-secondary py-2 pr-3 pl-8 text-sm text-text-primary outline-none transition-all focus:border-accent focus:ring-2 focus:ring-accent/20"
						>
							{colorOptions.map((color) => (
								<option key={color.value} value={color.value}>
									{color.label}
								</option>
							))}
						</select>
						<div
							className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3 h-3 w-3 rounded-full"
							style={{ backgroundColor: newActorColor }}
						/>
					</div>
					<Button type="submit" disabled={!newActorName.trim()}>
						<Plus className="h-4 w-4" />
						{m.settings_add_actor()}
					</Button>
				</form>
			</div>

			{/* List */}
			<div className="max-h-96 divide-y divide-border overflow-y-auto">
				{actors.length > 0 ? (
					actors.map((actor) => (
						<div
							key={actor.id}
							className="group flex items-center gap-3 px-6 py-3 transition-colors hover:bg-bg-hover/30"
						>
							{editingActorId === actor.id ? (
								<>
									<div
										className="h-3 w-3 flex-shrink-0 rounded-full"
										style={{ backgroundColor: editingActorColor }}
									/>
									<input
										type="text"
										value={editingActorName}
										onChange={(e) => setEditingActorName(e.target.value)}
										className="flex-1 rounded border border-accent bg-bg-secondary px-2 py-1 text-sm text-text-primary outline-none"
										onKeyDown={(e) => e.key === "Enter" && saveEditActor()}
									/>
									<select
										value={editingActorColor}
										onChange={(e) => setEditingActorColor(e.target.value)}
										className="cursor-pointer appearance-none rounded border border-border bg-bg-secondary px-2 py-1 text-xs"
									>
										{colorOptions.map((color) => (
											<option key={color.value} value={color.value}>
												{color.label}
											</option>
										))}
									</select>
									<button
										type="button"
										onClick={saveEditActor}
										className="rounded p-1 text-status-done hover:bg-status-done/10"
									>
										<Check className="h-4 w-4" />
									</button>
									<button
										type="button"
										onClick={cancelEditActor}
										className="rounded p-1 text-text-tertiary hover:bg-bg-hover"
									>
										<X className="h-4 w-4" />
									</button>
								</>
							) : (
								<>
									<div
										className="h-3 w-3 flex-shrink-0 rounded-full"
										style={{ backgroundColor: actor.color }}
									/>
									<span className="flex-1 text-sm text-text-primary">
										{actor.name}
									</span>
									<button
										type="button"
										onClick={() => startEditActor(actor)}
										className="rounded p-1 text-text-tertiary opacity-0 transition-all hover:bg-bg-hover hover:text-text-primary group-hover:opacity-100"
									>
										<Pencil className="h-4 w-4" />
									</button>
									<button
										type="button"
										onClick={() => handleDeleteActor(actor.id)}
										className="rounded p-1 text-text-tertiary opacity-0 transition-all hover:bg-bug/10 hover:text-bug group-hover:opacity-100"
									>
										<Trash2 className="h-4 w-4" />
									</button>
								</>
							)}
						</div>
					))
				) : (
					<div className="px-6 py-8 text-center text-sm text-text-tertiary">
						{m.settings_empty_actors()}
					</div>
				)}
			</div>
		</div>
	);
}
